import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import Toast from '../components/toast';
import { AnalysisResultToStateColor, isEmpty, ANALYSIS_RESULT, STATE_COLOR } from '../components/enums';
import SelectAllToolbar from '../components/select_all_toolbar';

// Material UI
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { styled } from '@mui/material/styles';

const StyledGridOverlay = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	'& .ant-empty-img-1': {
		fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
	},
	'& .ant-empty-img-2': {
		fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
	},
	'& .ant-empty-img-3': {
		fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
	},
	'& .ant-empty-img-4': {
		fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
	},
	'& .ant-empty-img-5': {
		fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
		fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
	},
}));

function NoLocationsOverlay() {
	return (
		<StyledGridOverlay>
			<svg
				width="120"
				height="100"
				viewBox="0 0 184 152"
				aria-hidden
				focusable="false"
			>
				<g fill="none" fillRule="evenodd">
					<g transform="translate(24 31.67)">
						<ellipse
							className="ant-empty-img-5"
							cx="67.797"
							cy="106.89"
							rx="67.797"
							ry="12.668"
						/>
						<path
							className="ant-empty-img-1"
							d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
						/>
						<path
							className="ant-empty-img-2"
							d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
						/>
						<path
							className="ant-empty-img-3"
							d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
						/>
					</g>
					<path
						className="ant-empty-img-3"
						d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
					/>
					<g className="ant-empty-img-4" transform="translate(149.65 15.383)">
						<ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
						<path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
					</g>
				</g>
			</svg>
			<Box sx={{ mt: 1 }}>No Locations</Box>
		</StyledGridOverlay>
	);
}

// Helper to generate a class name from a color
function colorToClassName(color) {
	if (!color) return '';
	return 'row-color-' + color.replace('#', '').toLowerCase();
}

// DataGrid getRowClassName implementation
const getRowClassName = (params) => {
	const color = AnalysisResultToStateColor(params.row.system_analysis);
	return color ? colorToClassName(color) : '';
};

export default function UserLocationManagement() {

	const { userId } = useParams();

	const [userInfo, setUserInfo] = useState({});

	const [permissionsLoading, setPermissionsLoading] = useState(true);
	const [locationsLoading, setLocationsLoading] = useState(true);

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});
	
	// Collection of Locations permissions the User to be modified has access to
	const [selfPermissions, setSelfPermissions] = useState([]);
	const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);

	// Collection of Locations information (Name, Serial, Org Info, etc.). This should only change if requesting user location access changes. This instance would require a page refresh
	const [locationsInfo, setLocationsInfo] = useState([]);
		
	// Data shown in the table
	const [tableData, setTableData] = useState([]);
	const [page, setPage] = useState(0); // DataGrid is 0-indexed
	const [pageSize, setPageSize] = useState(25);
	const [rowCount, setRowCount] = useState(0);
	const [sortModel, setSortModel] = useState([]);
	const [filterModel, setFilterModel] = useState({ items: [] });

	// Permissions management
	const [changesMade, setChangesMade] = useState(false);
	const [locationIDsChanged, setLocationIDsChanged] = useState([]);
	
	// Track all permission fields for each locationID
	const [modifiedPermissions, setModifiedPermissions] = useState({});

	const [selectAllState, setSelectAllState] = useState(true);

	const navigate = useNavigate();
	
	// Get the current user's locations permissions info
	useEffect(() => {
		GetSelfPermissions();
	}, []);

	// Get the selected user's info
	useEffect(() => {
		GetUserInfo();
		GetSelectedUserPermissions();
	}, [userId]);

	// Fetch paginated, sorted, filtered locations info
	useEffect(() => {
		GetLocationsInfo();
	}, [page, pageSize, sortModel, filterModel]);

	// Combine the permissions data and location info into table data
	useEffect(() => {
		BuildTableData();
	}, [selfPermissions, selectedUserPermissions, locationsInfo]);

	// Get the selected user's info
	function GetUserInfo() {
		axiosInstance.get(`/users/${userId}/`)
			.then(response => {
				// Check if the user is a superuser - no editing of superusers access
				if (response.data.is_superuser) {
					navigate('/403/', { replace: true });
				}

				if (isEmpty(response.data)) {
					navigate('/404/', { replace: true });
				}
				
				setUserInfo(response.data);
			})
			.catch(err => {
				console.error(err);
				navigate('/403/', { replace: true });
			});
	}

	// Get the current user's location permissions
	function GetSelfPermissions() {
		setPermissionsLoading(true);
		axiosInstance.get(`/users/locations/`)
			.then(response => {
				setSelfPermissions(response.data)
			})
			.catch(error => {
				console.error(error);
				setSelfPermissions([]);
			})
			.finally(() => {
				setPermissionsLoading(false);
			});
	}

	// Get the selected user's location permissions
	function GetSelectedUserPermissions() {
		setPermissionsLoading(true);
		axiosInstance.get(`/users/${userId}/locations/`)
			.then(response => {
				setSelectedUserPermissions(response.data)
			})
			.catch(error => {
				console.error(error);
				setSelectedUserPermissions([]);
			})
			.finally(() => {
				setPermissionsLoading(false);
			});
	}

	// Location information retrieval with server-side pagination, sorting, and filtering
	function GetLocationsInfo() {
		setLocationsLoading(true);

		// Build query params
		const params = {
			page_size: pageSize,
			page: page + 1, // API is 1-indexed
		};

		// sorting
		if (sortModel.length > 0) {
			params.sort_field = sortModel[0].field;
			params.sort_order = sortModel[0].sort;
		}
	
		// filtering
		if (filterModel.items.length > 0 && filterModel.items[0].value) {
			params.filter_field = filterModel.items[0].columnField;
			params.filter_value = filterModel.items[0].value;
		}

		axiosInstance.get(`/map/`, { params })
			.then(async (response) => {
				const { locations, count } = response.data;
				setLocationsInfo(locations);
				setRowCount(count);
			})
			.catch(error => {
				console.error(error);
				setLocationsInfo([]);
			})
			.finally(() => {
				setLocationsLoading(false);
			});
	}

	// Merge the current user location permissions, selected user location permissions, and location info to build the table
	function BuildTableData() {
		if (locationsInfo.length && selfPermissions.length && selectedUserPermissions.length) {
			setTableData(
				locationsInfo.map(location => {
					const selfLocPerm = selfPermissions.find(p => p.location_id === location.location_id) || {};
					const userLocPerm = selectedUserPermissions.find(p => p.location_id === location.location_id) || {};
					const modified = modifiedPermissions[location.location_id] || {};
					return {
						...location,
						...selfLocPerm,
						...userLocPerm,
						...modified,
						has_access: modified.has_access !== undefined ? modified.has_access : (userLocPerm.location_id !== undefined && userLocPerm.location_id !== null),
						change_system_settings: modified.change_system_settings !== undefined ? modified.change_system_settings : userLocPerm.change_system_settings,
						change_limits: modified.change_limits !== undefined ? modified.change_limits : userLocPerm.change_limits,
						change_email_settings: modified.change_email_settings !== undefined ? modified.change_email_settings : userLocPerm.change_email_settings,
						change_observer_settings: modified.change_observer_settings !== undefined ? modified.change_observer_settings : userLocPerm.change_observer_settings,
						view_audit_log: modified.view_audit_log !== undefined ? modified.view_audit_log : userLocPerm.view_audit_log,
						view_notes: modified.view_notes !== undefined ? modified.view_notes : userLocPerm.view_notes,
					};
				})
			);
		} else {
			setTableData([]);
		}
	}
		
	// Get user's name and/or email for the page title
	function getUserNameOrEmail() {
		if (!isEmpty(userInfo)) {
			if (userInfo.first_name && userInfo.last_name) {
				return userInfo.first_name + ' ' + userInfo.last_name + ' (' + userInfo.email + ')';
			} else if (userInfo.first_name) {
				return userInfo.first_name + ' (' + userInfo.email + ')';
			} else if (userInfo.last_name) {
				return userInfo.last_name + ' (' + userInfo.email + ')';
			} else {
				return userInfo.email;
			}
		}

		return '';
	}

	// Save changes made to user permissions
	function SaveChanges() {
		if (!changesMade || locationIDsChanged.length === 0) { return; }

		// Build the list of changed locations using modifiedPermissions
		let changedLocations = locationIDsChanged
			.map(locationId => {
				const perms = modifiedPermissions[locationId];

				if (!perms) return undefined;
				
				return {
					location_id: locationId,
					has_access: perms.has_access,
					change_system_settings: perms.change_system_settings,
					change_limits: perms.change_limits,
					change_email_settings: perms.change_email_settings,
					change_observer_settings: perms.change_observer_settings,
					view_audit_log: perms.view_audit_log,
					view_notes: perms.view_notes,
				};
			})
			.filter(Boolean);  // Remove undefined entries

		// Post the changes to the backend
		axiosInstance.post(`/users/${userId}/locations/change/`, changedLocations)
			.then(response => {
				let failedChanges = [];

				if (response.data && response.data.length > 0) {
					response.data.results.forEach(changeResult => {
						// Check if any changes failed
						if (!changeResult.success) {
							const failedLocation = locationsInfo.find(location => location.id === changeResult.location_id);
							if (failedLocation) {
								failedChanges.push({ serial: failedLocation.serial_number, reason: changeResult.error });
							}
						}
					});
				}

				// Display a toast message based on the success of permission changes
				if (failedChanges.length > 0) {
					setToast({ severity: 'error', message: `Failed to save changes for the following locations: \r\n${failedChanges.map(change => `SN${change.serial}: ${change.reason}`).join('\r\n')}`, open: true });
				} else {
					setToast({ severity: 'success', message: 'User permission changes saved successfully.', open: true });
				}
			})
			.catch(error => {
				console.error(error);
				setToast({ severity: 'error', message: 'An error occurred while saving changes. Please try again.', open: true });
			});
	
		setChangesMade(false);
		setLocationIDsChanged([]);
	
		// Update the table data
		GetSelectedUserPermissions();
	};

	// Append the location ID to the list of locations that have been updated (locationIDsChanged)
	const handleIDsAppend = (newInteger) => {
		setLocationIDsChanged(prevState => {
			if (!prevState.includes(newInteger)) {
				return [...prevState, newInteger];
			} else {
				return prevState;
			}
		});
	};

	// Handle the switch change event and update modifiedPermissions
	const handleSwitchChange = (locationId, switchName, event) => {
		setChangesMade(true);
		handleIDsAppend(locationId);

		// Change tracking for cross-page persistence
		setModifiedPermissions(prev => {
			const prevPerms = prev[locationId] || {};
			return {
				...prev,
				[locationId]: {
					...prevPerms,
					[switchName]: event.target.checked
				}
			};
		});

		// Live table state change
		setTableData(prevTableData => {
			return prevTableData.map(row => {
				if (row.location_id === locationId) {
					return { ...row, [switchName]: event.target.checked };
				} else {
					return row;
				}
			});
		});
	};

	// Handle the Select All button click
	const handleSelectAll = () => {
		setChangesMade(true);

		// Get all location IDs currently in the table
		const allLocationIds = tableData.map(row => row.location_id);

		// Update modifiedPermissions for all locations
		setModifiedPermissions(prev => {
			const updated = { ...prev };
			allLocationIds.forEach(locationId => {
				const prevPerms = prev[locationId] || {};
				updated[locationId] = {
					...prevPerms,
					has_access: selectAllState
				};
			});
			return updated;
		});

		// Track all changed IDs
		setLocationIDsChanged(prevState => {
			const newIds = allLocationIds.filter(id => !prevState.includes(id));
			return [...prevState, ...newIds];
		});

		// Update tableData for immediate UI feedback
		setTableData(prevTableData =>
			prevTableData.map(row => ({
				...row,
				has_access: selectAllState
			}))
		);

		// Toggle selectAllState for next click
		setSelectAllState(!selectAllState);
	};
	
	const handleSaveClick = () => {
		SaveChanges();
	};

	// Render a switch for a given locationID and switchName, using modifiedPermissions if present
	const renderSwitch = (locationId, switchName, checked, disabled = false) => {
		let tooltipText = locationsLoading ? "Please wait while all Locations data loads..." : "";

		if (disabled) {
			tooltipText = "You do not have permission to change this setting.";
		}

		// Use modifiedPermissions if present
		const value =
			modifiedPermissions[locationId] && modifiedPermissions[locationId][switchName] !== undefined
				? modifiedPermissions[locationId][switchName]
				: checked;

		return (
			<div style={{ width: '100%', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Tooltip title={tooltipText} placement="top">
					<Switch
						checked={value}
						onChange={(event) => handleSwitchChange(locationId, switchName, event)}
						color='primary'
						disabled={disabled || locationsLoading}
						sx={{ backgroundColor: '#ffffff77', borderRadius: '20px' }}
					/>
				</Tooltip>
			</div>
		);
	}

	function GetAnalysisIcon(status) {
		let icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
		let tooltip = "Unknown Status";

		if (
			status === ANALYSIS_RESULT.ANALYSIS_TEMP_SENSOR_ABOVE_DIFF_LIMIT ||
			status === ANALYSIS_RESULT.ANALYSIS_OUT_OF_RANGE_CRITICAL ||
			status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL ||
			status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL
		) {
			icon = <ErrorOutlineIcon style={{ color: 'black' }} />;
			tooltip = "Critical Analysis";
		} else if (
			status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS ||
			status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS
		) {
			icon = <WarningAmberOutlinedIcon style={{ color: STATE_COLOR.MAINTENANCE }} />;
			tooltip = "Maintenance Analysis";
		} else if (
			status === ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS
		) {
			icon = <CheckCircleOutlineOutlinedIcon style={{ color: STATE_COLOR.WITHIN_LIMITS }} />;
			tooltip = "Within Limits";
		} else {
			icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
			tooltip = "Unknown Status";
		}

		return <Tooltip title={tooltip} placement="top">{icon}</Tooltip>;
	}

	// User List table columns
	// TODO: MODIFY THE FUTURE PERMISSIONS TO DISABLE IF THE SELECTED USER DOES NOT HAVE ACCESS TO THE LOCATION
	// TODO: MODIFY THE FUTURE PERMISSIONS TO DISABLE IF THE ADMIN USER DOES NOT HAVE ACCESS TO THE PERMISSION THEMSELVES
	//  (E.G. IF THE ADMIN USER CANNOT CHANGE_SYSTEM_SETTINGS FOR A LOCATION, THEN THE CHANGE_SYSTEM_SETTINGS SWITCH SHOULD BE DISABLED FOR THAT LOCATION)
	const columns = [
		{
			field: 'system_analysis',
			headerName: 'Ana',
			headerAlign: "left",
			headerClassName: 'hidden-header',
			description: 'Overall System Analysis status',
			width: 55,
			flex: 0,
			filterable: false,
			sortable: false,
			renderCell: (params) => GetAnalysisIcon(params.value)
		},
		{
			field: 'has_access',
			headerName: 'Access',
			headerAlign: "center",
			description: 'Can the User view this location?',
			width: 85,
			flex: 0,
			filterable: false,
			sortable: false,
			hideable: false,
			renderCell: (params) => renderSwitch(params.row.location_id, 'has_access', params.value),
		},
		// {
		// 	field: 'change_system_settings',
		// 	headerName: 'System Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s system settings (not including limits)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_system_settings', params.value),
		// },
		// {
		// 	field: 'change_limits',
		// 	headerName: 'Change Limits',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s limit settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_limits', params.value),
		// },
		// {
		// 	field: 'change_email_settings',
		// 	headerName: 'Email Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s email settings (for all users)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_email_settings', params.value),
		// },
		// {
		// 	field: 'change_observer_settings',
		// 	headerName: 'Observer Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s observer settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'change_observer_settings', params.value),
		// },
		// {
		// 	field: 'view_audit_log',
		// 	headerName: 'View Audit Log',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Audit Log?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'view_audit_log', params.value),
		// },
		// {
		// 	field: 'view_notes',
		// 	headerName: 'View Notes',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Notes column across the site?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitch(params.row.userId, 'view_notes', params.value),
		// },
		{
			field: 'location_name',
			headerName: 'Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Location Name',
			minWidth: 250,
			flex: 1,
		},
		{
			field: 'serial_number',
			headerName: 'Serial #',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Serial Number',
			minWidth: 75,
			flex: 1,
		},
		{
			field: 'bvs_model',
			headerName: 'Model',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'BVS Controller Model',
			minWidth: 100,
			flex: 1,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Organization Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'region',
			headerName: 'Region',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Region Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'facility',
			headerName: 'Facility',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Facility Hierarchy',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'building',
			headerName: 'Building',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Building Hierarchy',
			minWidth: 150,
			flex: 1,
		},
	];
	
	return (
		<Paper
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				Editing Location Access for {getUserNameOrEmail()}
			</Typography>
			{locationsLoading && (
				<Box sx={{ width: '100%', mb: 1 }}>
					<LinearProgress />
				</Box>
			)}
			<style>{`
				.row-color-748592 { background-color: #74859299 !important; }
				.row-color-4b9c2e { background-color: #4b9c2e99 !important; }
				.row-color-f4b235 { background-color: #f4b23599 !important; }
				.row-color-f22635 { background-color: #f2263599 !important; }
			`}</style>
			<DataGrid
				getRowId={(row) => row.location_id}
				rows={tableData}
				columns={columns}
				loading={permissionsLoading || locationsLoading}
				autoPageSize={false}
				page={page}
				pageSize={pageSize}
				rowCount={rowCount}
				pagination
				paginationMode="server"
				onPageChange={(newPage) => setPage(newPage)}
				onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(0); }}
				sortingMode="server"
				sortModel={sortModel}
				onSortModelChange={setSortModel}
				filterMode="server"
				filterModel={filterModel}
				onFilterModelChange={setFilterModel}
				density='compact'
				getRowClassName={getRowClassName}
				components={{
					Toolbar: () => <SelectAllToolbar
						onSelectAll={handleSelectAll}
						selectAllState={selectAllState}
						columnName='Has Access'
						itemType='locations'
					/>,
					NoRowsOverlay: NoLocationsOverlay,
				}}
				sx={{
					padding: 2,
				}}
			/>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			{ changesMade && (
				<Paper style={{ padding: 10, margin: 1, border: '1px solid lightgray' }}>
					<Box display="flex" justifyContent="space-between" alignItems="right">
						<Typography variant="body1">You have unsaved changes. Would you like to save?</Typography>
						<Box>
							<Button variant="contained" color="primary" onClick={handleSaveClick}>
								Save
							</Button>
						</Box>
					</Box>
				</Paper>
			)}
		</Paper>
	);
}